import PropTypes from 'prop-types';
import queryString from 'query-string';

import { Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import CourierErrorFormContainer from '../Dashboard/CourierErrorFormContainer';
import GenericFormContainer from '../Forms/GenericFormContainer';

import NoteComponent from './NoteComponent';

import { formatSelect, lengthValidation250, required } from '../Forms/utils';
import { formatRecipients } from './utils';

import {
  BUTTON_SELECT_COMPONENT,
  CHECKBOX,
  CUSTOM_FIELD_COMPONENT,
  REACT_SELECT_INPUT,
  STYLED_TEXT,
} from '../Forms/fieldTypes';
import FrontDeskPackagesForm from '../FrontDesk/Packages/FrontDeskPackagesForm';

const deliveryNoteChildren = (deliveryNote) => (
  <NoteComponent note={deliveryNote} />
);

const AddDeliveryComponent = (props) => {
  const { t } = useTransl();
  const theme = useTheme();
  const {
    deliveryTypes,
    onSubmit,
    recipients,
    deliveryTypeOptions,
    isCallCenter,
    isFrontDesk,
    deliveryNote,
    form,
    initialValues,
    building,
    validate,
    formValues,
    accessPoint,
    openModal,
    closeModal,
    location,
    units,
  } = props;

  const { call_sid } = queryString.parse(location?.search);

  const isOtherDelivery = Number(formValues?.type) === 7;

  const formData = [
    {
      keyName: 'type',
      label: t('title.deliveryCompany'),
      fieldType: BUTTON_SELECT_COMPONENT,
      options: deliveryTypeOptions,
      validate: [required],
    },
    {
      keyName: 'pickUp',
      label: t('title.pickUp'),
      fieldType: CHECKBOX,
      validate: [],
    },
    {
      keyName: 'signatureRequired',
      label: t('title.needsSignature'),
      fieldType: CHECKBOX,
      validate: [],
      isHidden: true || !isCallCenter,
    },
    {
      keyName: 'recipients',
      label: t('title.recipient_s'),
      fieldType: REACT_SELECT_INPUT,
      options: formatRecipients(recipients, isCallCenter),
      multi: true,
      placeholder: t('delivery.filterBy'),
      validate: [],
      disabled: Boolean(formValues?.units?.length),
    },
    {
      keyName: 'units',
      label: t('delivery.units'),
      fieldType: REACT_SELECT_INPUT,
      options: formatSelect(units),
      sortAlphabetically: false,
      multi: true,
      validate: [],
      disabled: Boolean(formValues?.recipients?.length),
    },
    {
      keyName: 'comments',
      label: `${
        isOtherDelivery ? t('delivery.whatsDelivered') : t('title.details')
      }`,
      fieldType: STYLED_TEXT,
      multiline: true,
      validate: [lengthValidation250],
      placeholder: t('delivery.company'),
    },
    {
      keyName: 'deliveryNote',
      label: t('title.deliveryNote'),
      fieldType: CUSTOM_FIELD_COMPONENT,
      children: deliveryNoteChildren.bind(null, deliveryNote),
      isHidden: !isCallCenter || !deliveryNote,
    },
  ];
  const frontDeskProps = {
    render: (props) => (
      <FrontDeskPackagesForm
        {...props}
        deliveryTypeOptions={deliveryTypeOptions}
        recipients={recipients}
        units={units}
      />
    ),
    formData: [],
  };
  const generalProps = {
    isUpdate: false,
    canBeUpdated: false,
    submitText: t('delivery.addAndNotify'),
    backButton: false,
    isFullWidthForm: true,
    isFullWidthSubmit: true,
    customSubmitStyle: isCallCenter
      ? {
          backgroundColor: theme.palette.success.main,
          marginTop: '0.5rem',
        }
      : {},
    formData,
  };
  const genericFormContainerProps = isFrontDesk ? frontDeskProps : generalProps;

  return (
    <>
      <GenericFormContainer
        form={form}
        onSubmit={onSubmit}
        initialValues={initialValues}
        title={t('delivery.add')}
        validate={validate}
        building={building}
        deliveryTypes={deliveryTypes}
        accessPoint={accessPoint}
        enableReinitialize
        {...genericFormContainerProps}
      />
      {isCallCenter ? (
        <Button
          variant="contained"
          disableElevation
          fullWidth
          color="error"
          onClick={() =>
            openModal({
              title: t('title.courierError'),
              maxWidth: 'md',
              closeModal,
              render: () => <CourierErrorFormContainer call_sid={call_sid} />,
            })
          }
        >
          {t('title.courierError')}
        </Button>
      ) : null}
    </>
  );
};

AddDeliveryComponent.defaultProps = {
  formValues: {},
  accessPoint: {},
  validate: () => {},
  recipients: [],
  units: [],
  deliveryTypeOptions: [],
  deliveryNote: null,
  isCallCenter: false,
  isFrontDesk: false,
  closeModal: () => {},
  openModal: () => {},
  location: {},
};

AddDeliveryComponent.propTypes = {
  building: PropTypes.instanceOf(Object).isRequired,
  deliveryTypes: PropTypes.instanceOf(Array).isRequired,
  onSubmit: PropTypes.func.isRequired,
  form: PropTypes.string.isRequired,
  initialValues: PropTypes.instanceOf(Object).isRequired,
  accessPoint: PropTypes.instanceOf(Object),
  recipients: PropTypes.instanceOf(Array),
  units: PropTypes.instanceOf(Array),
  deliveryTypeOptions: PropTypes.instanceOf(Array),
  isCallCenter: PropTypes.bool,
  isFrontDesk: PropTypes.bool,
  deliveryNote: PropTypes.string,
  validate: PropTypes.func,
  formValues: PropTypes.instanceOf(Object),
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
  location: PropTypes.instanceOf(Object),
};

export default AddDeliveryComponent;
